import { useEffect, useRef, type ReactNode } from 'react';
import { ar } from '../ar';
import {
  IconAudit,
  IconClose,
  IconInspections,
  IconLogout,
  IconMenu,
  IconOverview,
  IconReports,
  IconRooms,
  IconSettings,
  IconShield,
  IconSupervisors,
  type IconProps,
} from './icons';

/**
 * The shell is where Linear contributes most: a narrow permanent rail that stays out of
 * the way, dense enough to hold seven destinations without scrolling, quiet enough that
 * the content is obviously the subject.
 *
 * It is authored for Arabic. The rail sits at the inline start — the right edge — which
 * is where the eye begins, not mirrored there afterwards.
 */

export type RouteId =
  | 'overview'
  | 'inspections'
  | 'reports'
  | 'rooms'
  | 'supervisors'
  | 'audit'
  | 'settings';

interface NavItem {
  id: RouteId;
  label: string;
  icon: (props: IconProps) => JSX.Element;
  hash: string;
}

/**
 * Every destination maps to a real endpoint or to data derived from one. Nothing here
 * is a placeholder for functionality that does not exist.
 *
 * Grouped rather than listed: seven flat rows read as a dump, three labelled groups
 * read as an organised product and give the rail a rhythm to sit in.
 */
export const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: ar.navGroupOperations,
    items: [
      { id: 'overview', label: ar.navOverview, icon: IconOverview, hash: '#/' },
      { id: 'inspections', label: ar.navInspections, icon: IconInspections, hash: '#/inspections' },
      { id: 'reports', label: ar.navReports, icon: IconReports, hash: '#/reports' },
    ],
  },
  {
    label: ar.navGroupProperty,
    items: [
      { id: 'rooms', label: ar.navRooms, icon: IconRooms, hash: '#/rooms' },
      { id: 'supervisors', label: ar.navSupervisors, icon: IconSupervisors, hash: '#/supervisors' },
    ],
  },
  {
    label: ar.navGroupAdmin,
    items: [
      { id: 'audit', label: ar.navAudit, icon: IconAudit, hash: '#/audit' },
      { id: 'settings', label: ar.navSettings, icon: IconSettings, hash: '#/settings' },
    ],
  },
];

/* -------------------------------------------------------------- Sidebar */

function SidebarContent({
  active,
  userName,
  onNavigate,
  onSignOut,
}: {
  active: RouteId;
  userName: string;
  onNavigate: (hash: string) => void;
  onSignOut: () => void;
}) {
  return (
    <>
      <div className="sidebar-brand">
        <span className="sidebar-mark" aria-hidden="true">
          <IconShield size={17} />
        </span>
        <span className="sidebar-brand-text">
          <span className="sidebar-name">ROOM VERIFY</span>
          <span className="sidebar-role">{ar.tagline}</span>
        </span>
      </div>

      <nav className="sidebar-nav" aria-label={ar.navLandmark}>
        {NAV_GROUPS.map((group) => (
          <div className="nav-group" key={group.label}>
            <span className="nav-group-label">{group.label}</span>
            <ul>
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = item.id === active;
                return (
                  <li key={item.id}>
                    <a
                      href={item.hash}
                      className={`nav-item${isActive ? ' is-active' : ''}`}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={(event) => {
                        event.preventDefault();
                        onNavigate(item.hash);
                      }}
                    >
                      <Icon size={17} />
                      <span className="nav-label">{item.label}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="sidebar-foot">
        <a
          href="#/settings"
          className="sidebar-user"
          onClick={(event) => {
            event.preventDefault();
            onNavigate('#/settings');
          }}
        >
          <span className="sidebar-avatar" aria-hidden="true">
            {userName.trim().charAt(0) || '؟'}
          </span>
          <span className="sidebar-user-text">
            <span className="sidebar-user-name">{userName}</span>
            <span className="sidebar-user-role">{ar.roleManagement}</span>
          </span>
        </a>
        <button className="icon-btn sidebar-signout" onClick={onSignOut} aria-label={ar.signOut}>
          <IconLogout size={17} />
        </button>
      </div>
    </>
  );
}

/* ------------------------------------------------------------- AppShell */

export function AppShell({
  active,
  userName,
  drawerOpen,
  onDrawerChange,
  onNavigate,
  onSignOut,
  children,
}: {
  active: RouteId;
  userName: string;
  drawerOpen: boolean;
  onDrawerChange: (open: boolean) => void;
  onNavigate: (hash: string) => void;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const drawerRef = useRef<HTMLDivElement>(null);

  // The drawer is a phone affordance: Escape closes it, and it never survives a
  // navigation.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDrawerChange(false);
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    drawerRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [drawerOpen, onDrawerChange]);

  const navigate = (hash: string) => {
    onDrawerChange(false);
    onNavigate(hash);
  };

  return (
    <div className="shell">
      <aside className="sidebar no-print">
        <SidebarContent
          active={active}
          userName={userName}
          onNavigate={navigate}
          onSignOut={onSignOut}
        />
      </aside>

      {drawerOpen && (
        <div
          className="scrim no-print drawer-scrim"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) onDrawerChange(false);
          }}
        >
          <div
            className="drawer layer"
            role="dialog"
            aria-modal="true"
            aria-label={ar.navLandmark}
            ref={drawerRef}
            tabIndex={-1}
          >
            <button
              className="icon-btn drawer-close"
              onClick={() => onDrawerChange(false)}
              aria-label={ar.close}
            >
              <IconClose size={18} />
            </button>
            <SidebarContent
              active={active}
              userName={userName}
              onNavigate={navigate}
              onSignOut={onSignOut}
            />
          </div>
        </div>
      )}

      <div className="shell-main">{children}</div>
    </div>
  );
}

/* ---------------------------------------------------------------- TopBar */

/**
 * Deliberately quiet. It carries the page's identity and at most two controls; anything
 * heavier belongs in the page, not in permanent chrome.
 */
export function TopBar({
  title,
  subtitle,
  onOpenDrawer,
  actions,
  breadcrumb,
}: {
  title: string;
  subtitle?: string;
  onOpenDrawer: () => void;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <header className="topbar no-print">
      <button className="icon-btn topbar-menu" onClick={onOpenDrawer} aria-label={ar.navLandmark}>
        <IconMenu size={19} />
      </button>

      <div className="topbar-identity">
        {breadcrumb}
        <h1 className="topbar-title">{title}</h1>
        {subtitle && <p className="topbar-subtitle">{subtitle}</p>}
      </div>

      {actions && <div className="topbar-actions">{actions}</div>}
    </header>
  );
}

/** In-page section heading — used inside a page, never in the top bar. */
export function PageSection({
  title,
  count,
  action,
  children,
  id,
}: {
  title: string;
  count?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  const headingId = id ? `${id}-title` : undefined;
  return (
    <section className="page-section" aria-labelledby={headingId}>
      <div className="section-head">
        <h2 id={headingId} className="section-title">
          {title}
          {count !== undefined && <span className="section-count num">{count}</span>}
        </h2>
        {action && <div className="section-action">{action}</div>}
      </div>
      {children}
    </section>
  );
}
